import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import {
  PERMISSIONS,
  lexwareArchiveUploadCreateSchema,
  type LexwareArchiveUpload,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { InlineHelp } from '@/features/help';
import {
  confirmLexwareArchiveUpload,
  createLexwareArchiveUpload,
  finishLexwareArchiveUpload,
  getLexwareArchiveUpload,
  retryLexwareArchiveUpload,
  lexwareArchiveUploadKey,
  lexwareArchiveUploadsKey,
  useLexwareArchiveUpload,
  useLexwareArchiveUploads,
} from '../api/use-lexware-archive-imports';
import {
  LexwareArchiveFileMismatch,
  lexwareArchiveFingerprint,
  uploadLexwareArchiveChunks,
} from '../lib/upload-lexware-archive';

export function LexwareArchiveImportPanel(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const client = useQueryClient();
  const panelId = useId();
  const fileId = useId();
  const controllerRef = useRef<AbortController | null>(null);
  const fingerprintRef = useRef<{ file: File; value: string } | null>(null);
  const [preparing, setPreparing] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const completedRef = useRef<string | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [file, setFile] = useState<File | null>(null);
  const [paused, setPaused] = useState(false);
  const [issue, setIssue] = useState<
    'invalidZip' | 'fileMismatch' | 'uploadInterrupted' | 'confirmError' | null
  >(null);
  const allowed =
    can(PERMISSIONS.IMPORTS_MANAGE) &&
    can(PERMISSIONS.IMPORTS_EXECUTE) &&
    can(PERMISSIONS.DMS_WRITE);
  const jobs = useLexwareArchiveUploads(open && allowed);
  const detail = useLexwareArchiveUpload(selectedId, open && allowed);
  const selected = detail.data ?? jobs.data?.data.find((job) => job.id === selectedId);
  const update = (job: LexwareArchiveUpload) => {
    client.setQueryData(lexwareArchiveUploadKey(job.id), job);
    setSelectedId(job.id);
  };
  const transfer = useMutation({
    mutationFn: async ({
      file: original,
      controller,
      id,
    }: {
      file: File;
      controller: AbortController;
      id: string | undefined;
    }) => {
      setPreparing(true);
      const fingerprint =
        fingerprintRef.current?.file === original
          ? fingerprintRef.current.value
          : await lexwareArchiveFingerprint(original, controller.signal);
      fingerprintRef.current = { file: original, value: fingerprint };
      setPreparing(false);
      if (controller.signal.aborted) return undefined;
      const job =
        id === undefined
          ? await createLexwareArchiveUpload(original, fingerprint, controller.signal)
          : { id };
      setSelectedId(job.id);
      const result = await uploadLexwareArchiveChunks(
        original,
        fingerprint,
        job.id,
        controller.signal,
        update,
      );
      if (
        !controller.signal.aborted &&
        result.status === 'UPLOADING' &&
        result.receivedBytes === result.fileSize
      )
        return finishLexwareArchiveUpload(result.id);
      return result;
    },
    onSuccess: (job) => {
      if (job !== undefined) update(job);
      void client.invalidateQueries({ queryKey: lexwareArchiveUploadsKey });
    },
    onError: (error) => {
      if (!controllerRef.current?.signal.aborted)
        setIssue(
          error instanceof LexwareArchiveFileMismatch ? 'fileMismatch' : 'uploadInterrupted',
        );
    },
    onSettled: () => {
      controllerRef.current = null;
      setPreparing(false);
    },
  });
  const recoverBackgroundStart = async (id: string) => {
    setIssue('confirmError');
    try {
      const job = await getLexwareArchiveUpload(id);
      update(job);
      if (['QUEUED', 'EXTRACTING', 'IMPORTING', 'COMPLETED'].includes(job.status)) setIssue(null);
    } catch {
      /* Keep the retryable user-facing error if status is also unavailable. */
    }
    void client.invalidateQueries({ queryKey: lexwareArchiveUploadsKey });
  };
  const confirm = useMutation({
    mutationFn: ({ id, previewHash }: { id: string; previewHash: string }) =>
      confirmLexwareArchiveUpload(id, previewHash),
    onSuccess: (job) => {
      update(job);
      setIssue(null);
      void client.invalidateQueries({ queryKey: lexwareArchiveUploadsKey });
    },
    onError: (_error, { id }) => recoverBackgroundStart(id),
  });
  const retry = useMutation({
    mutationFn: ({ id, previewHash }: { id: string; previewHash: string | null }) =>
      retryLexwareArchiveUpload(id, previewHash),
    onSuccess: (job) => {
      update(job);
      setIssue(null);
      void client.invalidateQueries({ queryKey: lexwareArchiveUploadsKey });
    },
    onError: (_error, { id }) => recoverBackgroundStart(id),
  });
  useEffect(() => () => controllerRef.current?.abort(), []);
  useEffect(() => {
    if (selected?.status === 'COMPLETED' && completedRef.current !== selected.id) {
      completedRef.current = selected.id;
      void client.invalidateQueries({ queryKey: ['legacy-documents'] });
    }
  }, [client, selected?.id, selected?.status]);
  if (!allowed) return null;
  const busy = transfer.isPending || confirm.isPending || retry.isPending;
  const uploadAvailable = selected === undefined || selected.status === 'UPLOADING';
  const canHash = typeof crypto?.subtle?.digest === 'function';
  const number = new Intl.NumberFormat(i18n.language);
  const size = (bytes: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'unit',
      unit: 'gigabyte',
      maximumFractionDigits: 2,
    }).format(bytes / 1_000_000_000);
  const compatible = selected?.status === 'UPLOADING' ? selected : undefined;
  const start = () => {
    if (file === null || !canHash) return;
    setIssue(null);
    setPaused(false);
    const controller = new AbortController();
    controllerRef.current = controller;
    transfer.mutate({ file, controller, id: compatible?.id });
  };
  const reset = () => {
    setSelectedId(undefined);
    setFile(null);
    fingerprintRef.current = null;
    setIssue(null);
    setPaused(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  return (
    <section className="space-y-3">
      <Button
        variant="outline"
        className="min-h-11"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        disabled={busy}
      >
        <Upload className="h-4 w-4" aria-hidden="true" />
        {t('legacy.import.open')}
      </Button>
      {open && (
        <div id={panelId} className="space-y-4 rounded-md border p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold">{t('legacy.import.title')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('legacy.import.description')}</p>
            </div>
            <InlineHelp
              articleId="sales.legacy-documents"
              anchor="lexware-upload"
              label={t('legacy.help')}
            />
          </div>
          <p className="text-sm text-muted-foreground">{t('legacy.import.separateData')}</p>
          {jobs.isPending && (
            <div role="status" aria-label={t('legacy.import.loading')} aria-busy="true">
              <Skeleton className="h-11 w-full" />
            </div>
          )}
          {jobs.isError && (
            <div role="alert" className="space-y-2 rounded-md border border-destructive p-3">
              <p>{t('legacy.import.historyError')}</p>
              <Button variant="outline" onClick={() => void jobs.refetch()}>
                {t('legacy.retry')}
              </Button>
            </div>
          )}
          {(jobs.data?.data.length ?? 0) > 0 && (
            <div className="space-y-1">
              <label htmlFor={`${panelId}-job`} className="text-sm font-medium">
                {t('legacy.import.job')}
              </label>
              <select
                id={`${panelId}-job`}
                className="min-h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={selectedId ?? ''}
                disabled={busy}
                onChange={(event) => {
                  reset();
                  setSelectedId(event.target.value || undefined);
                }}
              >
                <option value="">{t('legacy.import.newUpload')}</option>
                {jobs.data?.data.map((job) => (
                  <option value={job.id} key={job.id}>
                    {job.fileName} · {t(`legacy.import.statuses.${job.status}`)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {uploadAvailable && (
            <div className="space-y-3">
              <label htmlFor={fileId} className="block text-sm font-medium">
                {t('legacy.import.chooseZip')}
              </label>
              <Input
                ref={fileRef}
                id={fileId}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                disabled={busy}
                aria-invalid={issue === 'invalidZip' || issue === 'fileMismatch'}
                onChange={(event) => {
                  const chosen = event.target.files?.[0] ?? null;
                  if (
                    chosen !== null &&
                    !lexwareArchiveUploadCreateSchema
                      .omit({ fileFingerprint: true })
                      .safeParse({ fileName: chosen.name, fileSize: chosen.size }).success
                  ) {
                    setFile(null);
                    setIssue('invalidZip');
                    return;
                  }
                  setFile(chosen);
                  setIssue(null);
                  setPaused(false);
                }}
              />
              <p className="text-sm text-muted-foreground">{t('legacy.import.resumeHint')}</p>
              <p className="text-sm text-muted-foreground">{t('legacy.import.keepOpen')}</p>
              {preparing && (
                <p role="status" className="text-sm">
                  {t('legacy.import.preparing')}
                </p>
              )}
              {!canHash && (
                <p role="alert" className="text-sm text-destructive">
                  {t('legacy.import.browserUnsupported')}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  className="min-h-11"
                  disabled={file === null || busy || !canHash || jobs.isPending || jobs.isError}
                  onClick={start}
                >
                  {t(
                    compatible !== undefined || paused
                      ? 'legacy.import.resume'
                      : 'legacy.import.start',
                  )}
                </Button>
                {transfer.isPending && (
                  <Button
                    className="min-h-11"
                    variant="outline"
                    onClick={() => {
                      controllerRef.current?.abort();
                      setPaused(true);
                    }}
                  >
                    {t('legacy.import.pause')}
                  </Button>
                )}
              </div>
            </div>
          )}
          {issue !== null && (
            <p role="alert" className="rounded-md border border-destructive p-3 text-sm">
              {t(`legacy.import.errors.${issue}`)}
              {issue === 'confirmError' && selectedId !== undefined && (
                <Button
                  variant="outline"
                  className="ml-2"
                  onClick={() => void recoverBackgroundStart(selectedId)}
                >
                  {t('legacy.retry')}
                </Button>
              )}
            </p>
          )}
          {paused && (
            <p role="status" className="text-sm">
              {t('legacy.import.paused')}
            </p>
          )}
          {selectedId !== undefined && detail.isPending && selected === undefined && (
            <Skeleton className="h-16 w-full" />
          )}
          {detail.isError && (
            <div role="alert" className="space-y-2 text-sm">
              <p>{t('legacy.import.statusError')}</p>
              <Button variant="outline" onClick={() => void detail.refetch()}>
                {t('legacy.retry')}
              </Button>
            </div>
          )}
          {selected !== undefined && (
            <div className="space-y-4" aria-live="polite">
              <p className="break-words font-medium">{selected.fileName}</p>
              <p>{t(`legacy.import.statuses.${selected.status}`)}</p>
              {['QUEUED', 'EXTRACTING', 'IMPORTING', 'PREVIEW_READY'].includes(selected.status) && (
                <p className="text-sm text-muted-foreground">{t('legacy.import.canClose')}</p>
              )}
              {selected.status === 'UPLOADING' && (
                <div className="space-y-1">
                  <progress
                    aria-label={t('legacy.import.uploadProgress')}
                    className="h-3 w-full accent-primary"
                    max={selected.fileSize}
                    value={selected.receivedBytes}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t('legacy.import.bytesProgress', {
                      received: size(selected.receivedBytes),
                      total: size(selected.fileSize),
                    })}
                  </p>
                </div>
              )}
              {selected.status === 'PREVIEW_READY' && (
                <div className="space-y-3">
                  <h3 className="font-semibold">{t('legacy.import.preview')}</h3>
                  {selected.summaryJson === null || selected.previewHash === null ? (
                    <p role="alert">{t('legacy.import.previewUnavailable')}</p>
                  ) : (
                    <>
                      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <dt className="text-sm text-muted-foreground">
                            {t('legacy.import.counts.totalDocuments')}
                          </dt>
                          <dd>{number.format(selected.totalDocuments)}</dd>
                        </div>
                        {(
                          [
                            'textDocuments',
                            'imageOnlyDocuments',
                            'textErrors',
                            'truncatedDocuments',
                            'ignoredXortFiles',
                            'excludedFiles',
                            'unclassifiedDocuments',
                            'documentsWithCustomerNumber',
                            'documentsWithDocumentNumber',
                          ] as const
                        ).map((key) => (
                          <div key={key}>
                            <dt className="text-sm text-muted-foreground">
                              {t(`legacy.import.counts.${key}`)}
                            </dt>
                            <dd>{number.format(selected.summaryJson?.[key] ?? 0)}</dd>
                          </div>
                        ))}
                      </dl>
                      <h4 className="text-sm font-medium">{t('legacy.import.sourceTenants')}</h4>
                      <ul className="flex flex-wrap gap-4 text-sm">
                        {selected.summaryJson.sourceTenants.map((source) => (
                          <li key={source.key}>
                            <span>
                              {source.key === 'UNKNOWN' ? t('legacy.unknown') : source.key}
                            </span>{' '}
                            · {number.format(source.documentCount)}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-muted-foreground">
                        {t('legacy.import.confirmHint')}
                      </p>
                      <Button
                        className="min-h-11"
                        disabled={busy}
                        onClick={() => {
                          if (selected.previewHash !== null)
                            confirm.mutate({ id: selected.id, previewHash: selected.previewHash });
                        }}
                      >
                        {t(
                          confirm.isPending ? 'legacy.import.confirming' : 'legacy.import.confirm',
                        )}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {selected.status === 'IMPORTING' && (
                <div className="space-y-1">
                  <progress
                    aria-label={t('legacy.import.importProgress')}
                    className="h-3 w-full accent-primary"
                    max={Math.max(1, selected.totalDocuments)}
                    value={selected.importedDocuments}
                  />
                  <p className="text-sm">
                    {t('legacy.import.documentsProgress', {
                      imported: number.format(selected.importedDocuments),
                      total: number.format(selected.totalDocuments),
                    })}
                  </p>
                </div>
              )}
              {selected.status === 'FAILED' && (
                <div
                  role="alert"
                  className="space-y-2 rounded-md border border-destructive p-3 text-sm"
                >
                  <p>
                    {t(`legacy.import.failureCodes.${selected.errorCode ?? 'UNKNOWN'}`, {
                      defaultValue: t('legacy.import.failureCodes.UNKNOWN'),
                    })}
                  </p>
                  <p>{t('legacy.import.failedHint')}</p>
                  <Button
                    className="min-h-11"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      retry.mutate({ id: selected.id, previewHash: selected.previewHash })
                    }
                  >
                    {t(
                      retry.isPending ? 'legacy.import.retrying' : 'legacy.import.retryBackground',
                    )}
                  </Button>
                </div>
              )}
              {selected.status === 'COMPLETED' && (
                <Button
                  variant="outline"
                  className="min-h-11"
                  onClick={() => void client.invalidateQueries({ queryKey: ['legacy-documents'] })}
                >
                  {t('legacy.import.refreshArchive')}
                </Button>
              )}
              {(selected.status === 'COMPLETED' || selected.status === 'FAILED') && (
                <Button variant="ghost" className="min-h-11" disabled={busy} onClick={reset}>
                  {t('legacy.import.anotherUpload')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
